import { Component, OnInit, Output, EventEmitter, Input, Inject } from '@angular/core';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Observable, of, Subject } from 'rxjs';
import { LeadSourceOption } from 'app/models/dto/lead-source-option.model';
import { LeadSourceOptionService } from 'app/services/lead-source-option/lead-source-option.service';
import { DuplicateUserResponse, DuplicateUser, NewLead } from 'app/models/dto/add-new-lead';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { Select, Store } from '@ngxs/store';
import { IndustriesService } from 'app/services/industries/industries.service';
import { UpdateBorrower } from 'app/store/borrower/borrower/borrower.actions';
import { Router } from '@angular/router';
import { CreateNewBorrowerAlert } from 'app/store/borrower/alerts/borrower-alerts.actions';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { cloneDeep } from 'lodash';
import { LenderUser } from 'app/models/dto/lender-user.model';

@Component({
  selector: 'app-add-new-lead-form',
  templateUrl: './add-new-lead-form.component.html',
  styleUrls: ['./add-new-lead-form.component.scss', '../../scss/material/_dialog.scss']
})

export class AddNewLeadFormComponent implements OnInit {

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Output() newBusinessAlertDismissed = new EventEmitter<void>();
  @Output() callWrapUpSubmitted = new EventEmitter<void>();
  @Input() callWrapUp = false;

  loading$: Observable<boolean>;
  leadSourceOptions$: Observable<LeadSourceOption[]>;
  duplicateLead$ = new Subject();
  showErrorMessage$ = new Subject();

  addBusinessForm = new UntypedFormGroup({});
  formOptions: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];
  model: Partial<NewLead> = {
    email: '',
    leadSource: '',
    activationCode: undefined,
    first: '',
    last: '',
    title: '',
    phone: {
      countryCode: this.localeSettingService.getDefaultLocalePhoneCode(),
      phone: ''
    },
    phoneType: 'mobile',
    timeInBusiness: undefined,
    average_monthly_sales: undefined,
    creditScore: undefined,
    industryName: '',
    name: '',
    userId: undefined
  };

  isFranchise: boolean = false;
  currentUser: LenderUser;
  email: AbstractControl;
  phone: AbstractControl;
  isDuplicateUser = false;
  duplicateUser: DuplicateUser;
  duplicatePhoneNumber = false;
  duplicateEmail = false;
  addNewLeadFromSource = false;
  activationCodeMessage = '';
  activationCodeSuccessMessage = '';
  noLeadDataFound = '';
  activeUserAccount = { message: '', borrowerId: null };
  newLead = {} as NewLead;
  leadSourceOptions: LeadSourceOption[] = [];
  errMessage: string;
  navigateToNewBorrower: boolean = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private router: Router,
    private store: Store,
    private industriesService: IndustriesService,
    private leadSourceOptionService: LeadSourceOptionService,
    private dialogRef: MatDialogRef<AddNewLeadFormComponent>,
    private eventBusService: EventBusService,
    private localeSettingService: LocaleSettingService
  ) { }

  ngOnInit(): void {
    this.navigateToNewBorrower = this.data?.navigateToNewBorrower || this.navigateToNewBorrower;
    this.lenderUser$.pipe().subscribe(currentUser => {
      this.currentUser = cloneDeep(currentUser);
      if (this.currentUser && this.currentUser.team.type === 'franchise') {
        this.isFranchise = true;
      };
    });

    this.leadSourceOptions$ = this.leadSourceOptionService.get();
    this.leadSourceOptions$.subscribe(
      (options) => this.leadSourceOptions = options
    );

    this.fields = [
      {
        key: 'leadSource',
        wrappers: ['form-field'],
        type: 'autocomplete',
        templateOptions: {
          label: 'Lead Source',
          placeholder: 'search for a name or phone number',
          required: true,
          getOptions: (term) => this.filterLeadSourceOptions(term),
          attributes: {
            'data-cy': 'leadSource',
          },
        },
      },

      {
        key: 'adgroup',
        type: 'input',
        templateOptions: {
          label: 'Lead Source Details',
          required: false,
        },
        hideExpression: () => !this.isFranchise,
      },

      {
        key: 'activationCode',
        type: 'text',
        templateOptions: {
          label: 'Activation code (16 digit)',
          placeholder: 'XXXX XXXX XXXX XXXX',
          required: true,
          ngxMask: {
            mask: '0000 0000 0000 0000',
          },
          change: (activationCodeField) => {
            if (activationCodeField.formControl?.valid) {
              this.activeUserAccount = { message: '', borrowerId: null };

              this.leadSourceOptionService
                .verifyActivationCode(
                  activationCodeField.formControl.value.replace(/ /g, '')
                )
                .subscribe({
                  next: (response) => {
                    this.activationCodeMessage = '';
                    const answers = JSON.parse(response as string);

                    if (answers.message) {
                      this.activeUserAccount = {
                        message: answers.message,
                        borrowerId: answers.borrowerId,
                      };
                      this.newLead = {
                        ...this.newLead,
                        email: answers.email,
                      };
                      this.addBusinessForm
                        .get('email')
                        ?.setValue(answers.email);
                      return;
                    }

                    if (
                      !this.checkIsEmptyObject(answers.borrowerAnswers) ||
                      !this.checkIsEmptyObject(answers.infoAnswers)
                    ) {
                      this.newLead = {
                        ...this.newLead,
                        timeInBusiness:
                          answers.borrowerAnswers.timeInBusiness?.value ||
                          this.model.timeInBusiness,
                        average_monthly_sales:
                          answers.borrowerAnswers.average_monthly_sales
                            ?.value || this.model.average_monthly_sales,
                        creditScore:
                          answers.borrowerAnswers?.creditScore?.value ||
                          this.model.creditScore,
                        industryName:
                          answers.borrowerAnswers.lendioIndustry?.value ||
                          this.model.industryName,
                        first:
                          answers.infoAnswers.firstName?.value ||
                          this.model.first,
                        last:
                          answers.infoAnswers.lastName?.value ||
                          this.model.last,
                        mobilePhone:
                          answers.infoAnswers.phone?.value || this.model.phone,
                        zipId: answers.infoAnswers.businessZip?.value,
                      };

                      this.addBusinessForm
                        .get('name')
                        ?.setValue(answers.infoAnswers.businessName?.value);
                      this.addBusinessForm
                        .get('first')
                        ?.setValue(answers.infoAnswers.firstName?.value);
                      this.addBusinessForm
                        .get('last')
                        ?.setValue(answers.infoAnswers.lastName?.value);

                      this.activationCodeSuccessMessage =
                        'Successfully activated. Data has been filled.';

                      return;
                    }

                    if (
                      this.checkIsEmptyObject(answers.borrowerAnswers) &&
                      this.checkIsEmptyObject(answers.infoAnswers) &&
                      answers.questionHistory['basic-info']?.length === 0
                    ) {
                      this.noLeadDataFound = 'No lead data found.';
                    }
                  },
                  error: (error) => {
                    this.activationCodeMessage = error.message;
                  },
                });
            }
          },
        },
        hideExpression: () => {
          if (this.model.leadSource) {
            return this.model.leadSource !== 'Lendio Activation CL';
          }
          return true;
        },
        validation: {
          messages: {
            required: () =>
              'The selected LeadSource requires an activation code',
          },
        },
      },

      {
        key: 'name',
        type: 'input',
        templateOptions: {
          label: 'Business Name',
          required: true,
          attributes: {
            'data-cy': 'businessName',
          },
        },
      },

      {
        key: 'first',
        type: 'input',
        templateOptions: {
          label: 'Contact First Name',
          required: true,
          attributes: {
            'data-cy': 'contactFirstName',
          },
        },
      },

      {
        key: 'last',
        type: 'input',
        templateOptions: {
          label: 'Contact Last Name',
          required: true,
          attributes: {
            'data-cy': 'contactLastName',
          },
        },
      },

      {
        key: 'title',
        type: 'input',
        templateOptions: {
          label: 'Title/Role',
          attributes: {
            'data-cy': 'contactTitle',
          },
        },
      },

      {
        key: 'email',
        type: 'input',
        templateOptions: {
          type: 'email',
          label: 'Email',
          placeholder: 'Enter email',
          required: true,
          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i,
          attributes: {
            'data-cy': 'contactEmail',
          },
          change: (emailField) => {
            if (!this.duplicatePhoneNumber) {
              this.duplicateUser = {};
            }
            this.duplicateEmail = false;
            this.addNewLeadFromSource = false;
            this.duplicateLead$.next(false);
            if (emailField.formControl?.valid) {
              const emailAddress = emailField.formControl.value;
              this.showDuplicateUser(undefined, emailAddress);
            }
          },
        },
        validation: {
          messages: {
            pattern: 'Please enter valid email',
          },
        },
      },

      {
        fieldGroupClassName: 'phone',
        fieldGroup: [
          {
            className: 'phoneNumber',
            key: 'phone',
            type: 'intl-phone',
            fieldGroup: [
              {
                type: 'country-code-select',
                key: 'countryCode',
                className: 'formly-sub-column',
              },
              {
                type: 'phone-phone',
                key: 'phone',
                wrappers: [],
                className: 'formly-sub-column-2',
                props: {
                  label: 'Phone Number',
                  required: true,
                  attributes: {
                    'data-cy': 'phone-number-input',
                  },
                  change: (phoneField) => {
                    if (!this.duplicateEmail) {
                      this.duplicateUser = {};
                    }
                    this.duplicatePhoneNumber = false;
                    this.addNewLeadFromSource = false;
                    this.duplicateLead$.next(false);
                    if (phoneField.formControl?.valid) {
                      const phoneNumber = phoneField.formControl.value.replace(
                        /[^\d]/g,
                        ''
                      );

                      this.showDuplicateUser(phoneNumber);
                    }
                  },
                },
              },
            ],
          },
          {
            key: 'phoneType',
            type: 'select',
            className: 'phoneType',
            templateOptions: {
              label: 'Type',
              required: false,
              appearance: 'outline',
              options: [
                { value: 'mobile', label: 'mobile' },
                { value: 'office', label: 'office' },
                { value: 'home', label: 'home' },
                { value: 'other', label: 'other' },
              ],
            },
          },
        ],
      },
      {
        key: 'timeInBusiness',
        type: 'input',
        templateOptions: {
          label: 'How long have you been in business (Months)?',
          maxLength: 4,
          required: true,
          attributes: {
            'data-cy': 'timeInBusiness',
          },
        },
        hideExpression: () => this.hideOnClientReferral(),
      },

      {
        key: 'average_monthly_sales',
        type: 'currency',
        templateOptions: {
          label: 'Average Month Sales',
          required: true,
          attributes: {
            'data-cy': 'averageMonthlySales',
          },
        },
        expressionProperties: {
          'templateOptions.maskOptions': (model) => ({
            mask: 'separator',
            prefix:
              model.average_monthly_sales &&
              this.localeSettingService.getLocaleCurrencySymbol(),
          }),
        },
        hideExpression: () => this.hideOnClientReferral(),
      },

      {
        key: 'creditScore',
        type: 'input',
        templateOptions: {
          label: 'Personal Credit Score',
          minLength: 3,
          maxLength: 3,
          min: 300,
          max: 900,
          required: true,
          pattern: /^[0-9][0-9][0-9]*$/,
          attributes: {
            'data-cy': 'creditScore',
          },
        },
        hideExpression: () => this.hideOnClientReferral(),
        validation: {
          messages: {
            pattern: 'Please enter a value between 300 and 900',
          },
        },
      },

      {
        key: 'industryName',
        type: 'select',
        templateOptions: {
          label: 'Industry',
          required: true,
          appearance: 'outline',
          attributes: {
            'data-cy': 'industryName',
          },
          options: [
            ...this.getIndustriesOptions().map((industry) => ({
              label: industry.display,
              value: industry.value,
            })),
          ],
        },
        hideExpression: () => this.hideOnClientReferral(),
      },

      {
        key: 'zipId',
        type: 'input',
        templateOptions: {
          label: 'Business Zip',
          placeholder: 'Zip Code',
          maxLength: 5,
          required: true,
          pattern: /^[0-9]*$/,
        },
        hideExpression: () => !this.isFranchise,
      },
    ];
  }

  getIndustriesOptions() {
    return this.industriesService.getIndustries();
  }

  showDuplicateUser(phone?: string, email?: string) {
    let param = '';
    if (phone) {
      param = `?phone=${this.model.phone.countryCode === 2 ? '+1' + phone : phone}`;
    }
    if (email) {
      param = `?email=${email}`;
    }

    return this.leadSourceOptionService.getDuplicateUser(param).subscribe((res) => {
      if (res?.data) {
        if (phone) {
          this.duplicatePhoneNumber = true;
        } else {
          this.duplicateEmail = true;
        }
        const { data } = res as DuplicateUserResponse;
        this.isDuplicateUser = true;
        this.duplicateLead$.next(true);
        this.duplicateUser = {
          id: data.borrower.id,
          email: data.user.email,
          phone: data.borrower.phone,
          name: data.user.first + ' ' + data.user.last,
          deleted: data.borrower.deleted,
          industryName: data.borrower.industryName,
          title: data.borrower?.primary_contact?.title,
          businessName: data.borrower.name,
          user: data.user,
          borrower: data.borrower
        };
      }
    });
  }

  viewDuplicateBorrower() {
    if (this.duplicateUser.deleted && this.duplicateUser.id) {
      this.store.dispatch(new UpdateBorrower(this.duplicateUser.id, { deleted: false }));
      this.router.navigate(['/borrower/', this.duplicateUser.id]);
    }
  }

  updateExistingBorrower() {
    this.addBusinessForm.get('title')?.setValue(this.duplicateUser.title);
    this.addBusinessForm.get('phone')?.setValue(this.duplicateUser.phone);
    this.addBusinessForm.get('email')?.setValue(this.duplicateUser.email);
    this.addBusinessForm.get('name')?.setValue(this.duplicateUser.businessName);
    this.addBusinessForm.get('first')?.setValue(this.duplicateUser.name?.split(' ')[0]);
    this.addBusinessForm.get('last')?.setValue(this.duplicateUser.name?.split(' ')[1]);
    this.addBusinessForm.get('industryName')?.setValue(this.duplicateUser.industryName);
    this.duplicateEmail = false;
    this.duplicatePhoneNumber = false;
    this.addNewLeadFromSource = true;
  }

  checkIsEmptyObject(obj: {}) {
    return (Object.keys(obj).length === 0 && obj.constructor === Object);
  }

  showActivateUser() {
    this.router.navigate(['/borrower/', this.activeUserAccount.borrowerId]);
  }

  hideOnClientReferral(): boolean {
    if (this.model.leadSource) {
      return this.model.leadSource === 'Client Referral';
    }
    return false;
  }

  async saveNewLead($event) {
    const leadSourceOptionId = this.leadSourceOptions.find(
      (option) => this.isFranchise
        ? option.searchTerm.toLowerCase() === this.model.leadSource?.toLowerCase()
        : option.optLabel.toLowerCase() === this.model.leadSource?.toLowerCase()
    );

    const data = {
      ...$event,
      ...this.model,
      leadSourceOptionId: leadSourceOptionId?.id,
      average_monthly_sales: this.model.average_monthly_sales
        ?.toString()
        .replace(',', ''),
      importSource: 'pipeline',
      leadType: 'marketplace'
    };

    let comments = '';
    if ($event.bankruptcy) {
      comments += 'Bankruptcy info: Yes';
    }
    if ($event.notes?.pastFunds) {
      comments += '\n Applied for funds in the past: ' + $event.notes.pastFunds;
    }
    data.comments = comments;

    this.loading$ = of(true);

    if (this.duplicateUser.user && this.duplicateUser.borrower?.id) {
      return this.leadSourceOptionService.updateDuplicateLead(this.duplicateUser.user.id, data).subscribe({
        next: async (res) => await this.handleNewLeadSuccess(res),
        error: (err) => this.handleNewLeadError(err)
      });
    }

    if (this.isFranchise) {
      data.userId = this.currentUser.id;
      data.touchPoint = 'pipeline';
      data.medium = leadSourceOptionId?.medium;
      data.campaign = leadSourceOptionId?.campaign;
      data.lenderId = this.currentUser.id;
      data.mobilePhone = this.model.phone.phone;
      data.phoneCountryCodeId = this.model.phone.countryCode;
      data.mobilePhoneCountryCodeId = this.model.phone.countryCode;
      data.workPhoneCountryCodeId = this.model.phone.countryCode;

      return this.leadSourceOptionService.addFranchiseLead(data).subscribe({
        next: async (res) => await this.handleNewLeadSuccess(res),
        error: (err) => this.handleNewLeadError(err)
      });

    }

    data.mobilePhoneCountryCodeId = this.model.phone.countryCode;
    data.workPhoneCountryCodeId = this.model.phone.countryCode;
    data.phoneCountryCodeId = this.model.phone.countryCode;
    data.phone = this.model.phone.phone;

    return this.leadSourceOptionService.addNewBorrowerLead(data).subscribe({
      next: async (res) => await this.handleNewLeadSuccess(res),
      error: (err) => this.handleNewLeadError(err)
    });
  }

  async saveReferralLead() {
    const leadSourceOption = this.leadSourceOptions.find(
      (option) => option.optLabel.toLowerCase() === this.model.leadSource?.toLowerCase()
    );

    const data = {
      ...this.model,
      leadSourceOptionId: leadSourceOption?.id,
      mobilePhoneCountryCodeId: this.model.phone.countryCode,
      mobilePhone: this.model.phone.phone,
      userId: this.currentUser.id,
      average_monthly_sales: Number(this.model.average_monthly_sales?.toString().replace(',', ''))
    };

    if (leadSourceOption?.inboundCallActivity === 1) {
      data.touchPoint = 'inboundCall';
    } else {
      data.touchPoint = 'pipeline';
    }

    this.loading$ = of(true);

    data.phoneCountryCodeId = this.model.phone.countryCode;
    data.phone = this.model.phone.phone;

    return this.leadSourceOptionService.addReferralLead(data).subscribe({
      next: async (res) => await this.handleNewLeadSuccess(res),
      error: (err) => this.handleNewLeadError(err)
    });
  }

  filterLeadSourceOptions(term: string) {
    const leadSourceOptions = this.leadSourceOptions.filter(
      (option) => {
        if (this.isFranchise) {
          return option.searchTerm.toLowerCase().indexOf(term.toLowerCase()) !== -1;
        }
        return option.optLabel.toLowerCase().indexOf(term.toLowerCase()) !== -1;
      })
      .map((option) => {
        const val = this.isFranchise ? option.searchTerm : option.optLabel;
        return {
          label: val,
          value: val,
        };
      });
    return of(leadSourceOptions);
  }

  async handleNewLeadSuccess(res) {
    if (
      this.isFranchise
      && res.borrower?.userId !== this.currentUser.id
    ) {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: 'This lead is out of area. Please contact your Support Team if this is a special circumstance.',
        dismissDelay: 5000
      }));
    }

    this.loading$ = of(false),
    this.eventBusService.publish(new this.eventBusService.types.AddedNewLeadEvent);
    this.store.dispatch(new CreateNewBorrowerAlert({
      level: 'success',
      message: 'New business added! ',
      dismissDelay: 5000
    }));
    if (this.callWrapUp) {
      this.callWrapUpSubmitted.emit();
    } else {
      this.dialogRef.close(res.borrower);
    }

    if(this.navigateToNewBorrower) {
      this.router.navigate(['/borrower/', res.borrower.id]);
    }

  }

  handleNewLeadError(err: any) {
    this.loading$ = of(false);
    if (typeof err === 'string') {
      this.errMessage = err;
    } else if ('email' in err && typeof err.email === 'string') {
      this.errMessage = err.email;
    } else if ('original' in err && typeof err.original === 'string') {
      this.errMessage = err.original;
    } else {
      this.errMessage = 'Something went wrong, please check your data and try again.';
    }
    this.showErrorMessage$.next(true);
    this.store.dispatch(new CreateNewBorrowerAlert({
      level: 'error',
      message: 'Failed to create new lead.',
      dismissDelay: 5000
    }));
  }
}
